import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import IconPlusCircled from 'icons/icon-plus-circled';
import IconCheckCircled from 'icons/icon-check-circled';

import { TEMPLATE_OPTIONS_MAP } from 'constants/template';

import { Template, TemplateOptionType } from 'types';

interface OptionProps {
    id: TemplateOptionType,
    label: string,
    description: string,
    selected: boolean,
    onToggle: (id: TemplateOptionType) => void
}

const Option = ({ id, label, description, selected, onToggle }: OptionProps) => {
    const handleClick = () => {
        onToggle(id);
    }

    return (
        <div className={'entry' + (selected ? ' selected' : '')} onClick={handleClick}>
            <div className="entry-head">
                { selected ? <IconCheckCircled /> : <IconPlusCircled /> }
            </div>

            <div className="entry-body">
                <h5>{ label }</h5>

                <p>{ description }</p>
            </div>
        </div>
    );
}


interface TemplateOptionsProps {
    type: Template['type'],
    value?: Template['options'],
    disabledOptions?: TemplateOptionType[],
    onChange: (selection: Template['options']) => void
}

const TemplateOptions = ({ type, value, disabledOptions, onChange }: TemplateOptionsProps) => {
    const { t } = useTranslation(['template']);
    const [selection, setSelection] = useState<Template['options']>([]);

    const options = useMemo(() => {
        const result: Template['options'] = [];
        TEMPLATE_OPTIONS_MAP[type].forEach(option => {
            if (!disabledOptions || !disabledOptions?.includes(option)) {
                result.push(option)
            }
        });

        return result;
    }, [type, disabledOptions])

    const handleToggle = useCallback((id: TemplateOptionType) => {
        let nextSelection: Template['options'];

        if (selection.includes(id)) {
            nextSelection = selection.filter(i => i !== id);
        } else {
            nextSelection = [...selection, id];
        }

        setSelection(nextSelection);
        onChange(nextSelection);
    }, [selection, onChange]);

    useEffect(() => {
        setSelection(value || options);
        onChange(value || options);
    }, [options]);

    return (
        <div className="selection-list selection-list-columns selection-list-alt">
            {
                options.map(id => <Option
                    key={id}
                    id={id}
                    label={ t(`options.${type}.${id}.title`) }
                    description={ t(`options.${type}.${id}.description`) }
                    selected={selection.includes(id)}
                    onToggle={handleToggle}
                />)
            }
        </div>
    );
}

export default TemplateOptions;
